import { Action, Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { ThunkAction } from 'redux-thunk';
import { IAppState } from './reducers';
import { AssetHit } from '../api/vault/searchEngine/interfaces';
import { PrePostRoll, ROLL } from '../components/VirtualClipper/state/prePostRollSelection';
import { getVideoSourceFromPreview, getAssetFromApi } from '../components/AssetDetails/api/assetsLookupsMetatags';
import { selectSelectedClips } from '../components/VirtualClipper/state/clipList';
import { selectPreviewedAssetSource } from './previewAsset';
import { selectSource } from '../components/VirtualClipper/state/clipperMediaContent';

export interface IThumbnailFrame {
  clipIndex: number,
  time: number,
}

export interface IState {
  preRoll: {
    asset: AssetHit,
    duration: number,
    url: string,
  },
  postRoll: {
    asset: AssetHit,
    duration: number,
    url: string,
  },
  isPrePostRollLoading: boolean,
  thumbnailFrame: IThumbnailFrame,
}

export enum ActionType {
  SetPrePostRoll = 'SelectedClipsPreview.setPrePostRoll',
  RemovePrePostRoll = 'SelectedClipsPreview.removePrePostRoll',
  StartPrePostRollLoading = 'SelectedClipsPreview.startPrePostRollLoading',
  SetThumbnailFrame = 'SelectedClipsPreview.setThumbnailFrame',
  ResetSelectedClipsPreview = 'SelectedClipsPreview.reset',
}
export interface ISelectedClipsPreviewAction extends Action<ActionType> {
  payload?: any
}

type ISelectedClipsPreviewThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  IAppState,
  unknown,
  ISelectedClipsPreviewAction
>

const INITIAL_STATE: IState = {
  preRoll: null,
  postRoll: null,
  isPrePostRollLoading: false,
  thumbnailFrame: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: ISelectedClipsPreviewAction): IState {
  switch (action.type) {
    case ActionType.SetPrePostRoll:
      return {
        ...state,
        isPrePostRollLoading: false,
        [action.payload.type === ROLL.PRE ? 'preRoll' : 'postRoll']: {
          asset: action.payload.asset,
          duration: action.payload.duration,
          url: action.payload.url,
          sha1: action.payload.sha1,
        },
      };
    case ActionType.StartPrePostRollLoading:
      return {
        ...state,
        isPrePostRollLoading: true,
      };
    case ActionType.RemovePrePostRoll:
      return {
        ...state,
        [action.payload.type === ROLL.PRE ? 'preRoll' : 'postRoll']: null,
      };
    case ActionType.SetThumbnailFrame:
      return {
        ...state,
        thumbnailFrame: action.payload,
      };
    case ActionType.ResetSelectedClipsPreview:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const resetSelectedClipsPreview = () => ({
  type: ActionType.ResetSelectedClipsPreview,
});

const selectPreRoll = (state: IAppState) => state.selectedClipsPreview.preRoll;
const selectPostRoll = (state: IAppState) => state.selectedClipsPreview.postRoll;

export const selectSelectedClipsWithPrePostRolls = createSelector(
  selectSelectedClips,
  selectPreRoll,
  selectPostRoll,
  selectSource,
  (selectedClips, preRoll, postRoll, previewedAssetSource) => {
    const clips: any[] = [];
    if (preRoll) {
      clips.push({
        markInTime: 0,
        markOutTime: preRoll.duration,
        url: preRoll.url,
      });
    }

    if (selectedClips) {
      selectedClips.forEach((clip: any) => {
        clips.push({
          markInTime: clip.markInTime,
          markOutTime: clip.markOutTime,
          url: previewedAssetSource,
        });
      });
    }

    if (postRoll) {
      clips.push({
        markInTime: 0,
        markOutTime: postRoll.duration,
        url: postRoll.url,
      });
    }

    return clips;
  },
);

export const selectClipStartingPoints = createSelector(
  selectSelectedClipsWithPrePostRolls,
  (clips) => {
    const clipStartingPoints: number[] = [];
    if (!clips) return clipStartingPoints;

    clips.reduce((duration: number, currentValue: any) => {
      clipStartingPoints.push(duration);
      return duration + currentValue.markOutTime - currentValue.markInTime;
    }, 0);

    return clipStartingPoints;
  },
);

export const selectTotalClipsDuration = createSelector(
  selectSelectedClipsWithPrePostRolls,
  (clips) => (clips ? clips.reduce((duration: number, currentValue: any) => duration + currentValue.markOutTime - currentValue.markInTime, 0) : 0),
);

export const selectMarksForPublishPreviewPlayer = createSelector(
  selectClipStartingPoints,
  (clipStartingPoints) => (clipStartingPoints ? clipStartingPoints.reduce((marks: { value: number }[], startingPoint, index) => {
    if (index !== 0) {
      marks.push({
        value: startingPoint,
      });
    }
    return marks;
  }, []) : []),
);

export function selectIsPrePostRollLoading(state: IAppState) {
  return state.selectedClipsPreview.isPrePostRollLoading;
}

export function setThumbnailFrame(thumbnailFrame: IThumbnailFrame) {
  return {
    type: ActionType.SetThumbnailFrame,
    payload: thumbnailFrame,
  };
}

export function removeThumbnailFrame() {
  return {
    type: ActionType.SetThumbnailFrame,
    payload: INITIAL_STATE.thumbnailFrame,
  };
}

export function selectThumbnailFrame(state: IAppState) {
  return state.selectedClipsPreview.thumbnailFrame;
}

export const updateThumbnailOnPrePostRollChange = (type: PrePostRoll, asset: AssetHit) => (
  dispatch: Dispatch<ISelectedClipsPreviewAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const { preRoll, postRoll, thumbnailFrame } = state.selectedClipsPreview;

  const isThumbnailSet = thumbnailFrame !== null;
  const isRemovingPrePostRoll = asset === null;
  const isAddingPrePostRoll = asset !== null;

  if (isThumbnailSet) {
    if (type === ROLL.PRE) {
      const isPreRollSet = preRoll !== null;
      const isThumbnailSetOnPreRoll = thumbnailFrame.clipIndex === 0;

      if (isPreRollSet) {
        if (isThumbnailSetOnPreRoll) {
          dispatch(removeThumbnailFrame());
        } else if (isRemovingPrePostRoll) {
          dispatch({
            type: ActionType.SetThumbnailFrame,
            payload: {
              ...thumbnailFrame,
              clipIndex: thumbnailFrame.clipIndex - 1,
            },
          });
        }
      } else if (isAddingPrePostRoll) {
        dispatch({
          type: ActionType.SetThumbnailFrame,
          payload: {
            ...thumbnailFrame,
            clipIndex: thumbnailFrame.clipIndex + 1,
          },
        });
      }
    } else {
      const isPostRollSet = postRoll !== null;
      const selectedClipsWithPrePostRolls = selectSelectedClipsWithPrePostRolls(state);
      const isThumbnailSetOnPostRoll = thumbnailFrame.clipIndex === selectedClipsWithPrePostRolls.length - 1;

      if (isPostRollSet && isThumbnailSetOnPostRoll) {
        dispatch(removeThumbnailFrame());
      }
    }
  }
};

export const setPrePostRoll = (type: PrePostRoll, asset: AssetHit): ISelectedClipsPreviewThunk => async (
  dispatch,
) => {
  dispatch({
    type: ActionType.StartPrePostRollLoading,
  });
  const videoUrl = await getVideoSourceFromPreview(asset._id);
  const assetDetails = await getAssetFromApi(asset._id);

  const video = document.createElement('video');
  video.preload = 'metadata' as 'auto' | 'metadata' | 'none';
  video.onloadedmetadata = () => {
    dispatch(updateThumbnailOnPrePostRollChange(type, asset));

    dispatch({
      type: ActionType.SetPrePostRoll,
      payload: {
        type,
        asset,
        duration: video.duration,
        url: videoUrl,
        sha1: assetDetails.sha1,
      },
    });
  };

  video.src = videoUrl;
};

export const removePrePostRoll = (type: PrePostRoll): ISelectedClipsPreviewThunk => async (
  dispatch,
) => {
  dispatch(updateThumbnailOnPrePostRollChange(type, null));

  dispatch({
    type: ActionType.RemovePrePostRoll,
    payload: {
      type,
    },
  });
};
